import React from 'react';
import { useLotImage } from '../../utils/getLotImage';
import { TabKind } from './types';
import { useCommandeMsData } from './hooks/useCommandeMsData';
import { useCommandeMsTabs } from './hooks/useCommandeMsTabs';
import { useCommandeMsUnreadCounts } from './hooks/useCommandeMsUnreadCounts';
import CommandeMsHeader from './components/CommandeMsHeader';
import CommandeMsTabs from './components/CommandeMsTabs';
import CommandeMsContent from './components/CommandeMsContent';
import './SuiviDeCommandeMSV2.css';
import { scrollToTop } from '../../utils/scroll';

// Skeleton plein ecran (chargement) : silhouette banniere + 6 onglets + contenu, dans les MEMES
// conteneurs (.scms / .scms__content / .scms__tab-content) et avec la meme geometrie que le vrai
// rendu -> aucun saut de layout a l'arrivee des donnees. Purement decoratif.
const CommandeMSSkeleton: React.FC = () => (
  <div className="scms scms-skeleton" aria-hidden="true">
    <div className="scms-sk-banner">
      <div className="scms-sk-back"><span className="scms-sk scms-sk-circle" /></div>
      <div className="scms-sk-h1"><span className="scms-sk" /></div>
      <div className="scms-sk-h2"><span className="scms-sk" /></div>
      <div className="scms-sk-subtitle"><span className="scms-sk" /></div>
      <div className="scms-sk-statusline"><span className="scms-sk" /></div>
    </div>
    <div className="scms__content">
      <div className="scms-sk-tabs">
        {Array.from({ length: 6 }).map((_, i) => (
          <span key={i} className="scms-sk scms-sk-tab" />
        ))}
      </div>
      <div className="scms__tab-content">
        <div className="scms-sk-content">
          <span className="scms-sk scms-sk-block" />
          <span className="scms-sk scms-sk-block" />
          <span className="scms-sk scms-sk-block scms-sk-block--lg" />
        </div>
      </div>
    </div>
  </div>
);

// Page PRESTATAIRE commande-ms (v2). Orchestration mince : data -> onglets -> rendu.
const SuiviDeCommandeMSV2: React.FC = () => {
  const { commande, loading, error, refetch } = useCommandeMsData();
  const lotImage = useLotImage(commande?.operation?.lot_technique?.nom);
  const { descriptors, activeIndex, activeTab, setActiveIndex } = useCommandeMsTabs(commande);
  const { counts: unreadCounts, markRead } = useCommandeMsUnreadCounts(commande?.operation?.id);

  // Efface le badge du canal des qu'on active son onglet (couvre la race fetch/changement d'onglet).
  const activeKind = activeTab?.kind;
  React.useEffect(() => {
    if (activeKind === 'questions_reponses' || activeKind === 'messagerie') markRead(activeKind);
  }, [activeKind, unreadCounts, markRead]);

  // Bascule d'onglet par `kind` (ex. Consultation -> Offre, Notifications -> Messagerie). Remonte en
  // haut de page : les onglets sont longs, sinon on arrive au milieu du nouveau. Point unique pour
  // TOUS les boutons de bascule de la case (la barre d'onglets, elle, est deja en haut).
  const goToTab = React.useCallback(
    (kind: TabKind) => {
      const index = descriptors.findIndex((d) => d.kind === kind && !d.disabled && !d.inactive);
      if (index >= 0) {
        setActiveIndex(index);
        scrollToTop();
      }
    },
    [descriptors, setActiveIndex],
  );

  if (loading) {
    return <CommandeMSSkeleton />;
  }

  if (error && !commande) {
    return (
      <div className="scms__error">
        <p>Erreur: {error}</p>
        <p>Impossible de charger les détails de ce marché subséquent.</p>
      </div>
    );
  }

  if (!commande) {
    return <div className="scms__empty">Aucune commande MS trouvée avec cet identifiant.</div>;
  }

  const lotTechnique = commande.operation?.lot_technique;
  const idLotDepartement = commande.operation?.id_lot_departement;
  const departement = commande.operation?.lot_departement?.departement;
  const subtitle = lotTechnique
    ? `${lotTechnique.id} - ${lotTechnique.nom}, ${idLotDepartement} - ${departement?.nom ?? ''}`
    : 'Informations de lot non disponibles';

  return (
    <div className="scms">
      <div className="no-print">
        <CommandeMsHeader
          titre={commande.operation?.nom ?? 'Marché subséquent'}
          sousTitre=""
          subtitle={subtitle}
          commande={commande}
          backgroundImage={lotImage}
        />
      </div>

      <div className="scms__content">
        <CommandeMsTabs
          descriptors={descriptors}
          activeIndex={activeIndex}
          onSelect={setActiveIndex}
          unreadCounts={unreadCounts}
        />
        <div className="scms__tab-content">
          <CommandeMsContent
            commande={commande}
            activeTab={activeTab}
            refetch={refetch}
            goToTab={goToTab}
            onMarkRead={markRead}
          />
        </div>
      </div>
    </div>
  );
};

export default SuiviDeCommandeMSV2;
